import { c } from 'ttag';

import { refusalCode } from '../../../graphql/refusalCode';
import { Refusal } from '../../accounts/portalAccess';

/**
 * Reading of the refusals `inviteUser` answers with. The wording is ours: the API's
 * refusal strings are log notes, not messages. Codes are grouped into four families
 * that decide the shape of the answer (search again / one gesture repairs / about the
 * actor / malformed draft).
 *
 * The server's own sentence is kept as `detail` only where the refusal names an object
 * the front does not hold (the refused accounts, a role or catalogue entry): `formatError`
 * strips every extension but `code`, so account names travel in the message text, and
 * only the first refusal's code travels when several accounts are refused.
 */

/** The server's own sentence. Kept verbatim: paraphrasing a technical note loses it. */
const serverAnswer = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/**
 * Family 1: the contact resolved at step 1. The identity may have moved since the
 * search, so the guidance is to search again, never to retry.
 */
const contactRefusal = (code: string | null): Refusal | null => {
    switch (code) {
        case 'CONTACT_NOT_FOUND':
            return {
                title: c('Onboarding invite refusal').t`This contact no longer exists.`,
                guidance: c('Onboarding invite refusal')
                    .t`It has disappeared from the source since it was picked at step 1. Go back to step 1 and search the address again: what comes back now is what can be onboarded.`,
            };
        case 'CONTACT_EMAIL_MISMATCH':
            // The address moved after the search; the gesture is to search again, not
            // to correct the contact.
            return {
                title: c('Onboarding invite refusal').t`The address being invited is not the one this contact carries.`,
                guidance: c('Onboarding invite refusal')
                    .t`In practice the address changed after the search, so the identity on this journey belongs to someone else. Go back to step 1, search the address as it stands now, and pick the contact it designates. There is nothing to correct on the contact itself: the identity comes from the source.`,
            };
        case 'CONTACT_ALREADY_LINKED':
            // No gesture on this screen repairs it: the source must be corrected.
            return {
                title: c('Onboarding invite refusal').t`This contact already designates another portal user.`,
                guidance: c('Onboarding invite refusal')
                    .t`A contact is onboarded once, and this one was onboarded for somebody else — which happens when the source changes the address of a contact already onboarded. Nothing on this screen repairs it: searching again will return the same contact. Have the source corrected, then start the onboarding over.`,
            };
        default:
            return null;
    }
};

/**
 * Family 2: a state of the data, and one gesture that lifts it. Every entry names where
 * that gesture is made, because none of them is made here.
 */
const dataStateRefusal = (code: string | null, server: string): Refusal | null => {
    switch (code) {
        case 'MEMBERSHIP_ALREADY_EXISTS':
            return {
                title: c('Onboarding invite refusal')
                    .t`This person is already attached to one of the selected accounts.`,
                guidance: c('Onboarding invite refusal')
                    .t`An invitation adds attachments and never rewrites the ones already there, so the role and the perimeter of an existing attachment are changed from the person's own sheet rather than from here. Take that account out of the selection at step 2 and send again — or, if it was the only one, there is nothing left to invite. The accounts concerned are named below.`,
                detail: server,
            };
        case 'PORTAL_CLOSED_ON_ACCOUNT':
            // Most accounts are closed to the portal, so this is the ordinary answer,
            // not an incident; the copy says so.
            return {
                title: c('Onboarding invite refusal').t`One of the selected accounts is not open to the portal.`,
                guidance: c('Onboarding invite refusal')
                    .t`Attaching somebody to a closed account would open an access onto nothing. Opening an account to the portal is a separate gesture, taken on the account itself, and it comes first — otherwise take that account out of the selection at step 2. Most accounts are closed today, so this answer is the ordinary one rather than a sign that something is broken. The accounts concerned are named below.`,
                detail: server,
            };
        case 'DIFFERENT_ROOT_ORGANIZATION':
            return {
                title: c('Onboarding invite refusal').t`These accounts do not belong to the same client.`,
                guidance: c('Onboarding invite refusal')
                    .t`A person on the client side belongs to a single client — the root organisation — and may be attached to several accounts of its tree, never across two. Either the selection at step 2 mixes two clients, or this person already holds an access at another client which has to be closed before one is opened here. The server's answer below names what is in the way.`,
                detail: server,
            };
        case 'MARKET_RESTRICTION_INCOMPATIBLE':
            return {
                title: c('Onboarding invite refusal')
                    .t`A perimeter restricted to a few markets cannot hold on one of these accounts.`,
                guidance: c('Onboarding invite refusal')
                    .t`A module opened on that account cannot honour a restriction by market: part of what it lists carries no market at all, so those lists would silently lose rows. Set that line back to « the whole account » at step 2, or have the module closed on the account first. The account and the modules in the way are named below.`,
                detail: server,
            };
        case 'ORGANIZATION_NOT_FOUND':
            // Names an account, and only an account. The server's sentence stays: a
            // draft carries N accounts and only the refused one is named there.
            return {
                title: c('Onboarding invite refusal').t`One of the selected accounts no longer exists.`,
                guidance: c('Onboarding invite refusal')
                    .t`It has gone from the source since it was picked, and an attachment cannot hang from an account that is no longer there. Reload the page and compose the selection again from what it offers now. The account concerned is named below.`,
                detail: server,
            };
        case 'DASHBOARD_NOT_FOUND':
            // The row itself has gone, nothing to put back; distinct from
            // `DASHBOARD_ARCHIVED` below, where the entry exists and can be restored.
            return {
                title: c('Onboarding invite refusal').t`A dashboard picked at step 3 is not in the catalogue at all.`,
                guidance: c('Onboarding invite refusal')
                    .t`No entry carries the identifier this draft points at. Withdrawing an entry archives it rather than deleting it, so this is the rarer case: the entry itself has gone, and there is nothing to have put back. Go back to step 3, where the catalogue is served again, and pick from what it offers now. This step is optional: inviting without any dashboard is an ordinary outcome. The identifier refused is in the answer below.`,
                detail: server,
            };
        case 'DASHBOARD_ARCHIVED':
            return {
                title: c('Onboarding invite refusal')
                    .t`A dashboard picked at step 3 has been withdrawn from the catalogue.`,
                guidance: c('Onboarding invite refusal')
                    .t`Somebody archived it since step 3 was left, and an archived entry is no longer grantable. Two ways out: have it put back in the catalogue of its account, which today means declaring it again since no screen un-archives an entry, or go back to step 3 and pick another one. This step is optional: inviting without any dashboard is an ordinary outcome. The entry is named below.`,
                detail: server,
            };
        case 'PARTNER_LIST_NOT_FOUND':
            return {
                title: c('Onboarding invite refusal').t`The partner list picked at step 4 has left the catalogue.`,
                guidance: c('Onboarding invite refusal')
                    .t`Somebody has archived it since step 4 was left, and an archived list is no longer assignable. Go back to step 4 and pick another one — or none at all, attributing no list being a normal outcome of this journey.`,
            };
        case 'ACCOUNT_DISABLED':
            return {
                title: c('Onboarding invite refusal').t`This person's portal account is disabled.`,
                guidance: c('Onboarding invite refusal')
                    .t`They already exist on the portal and their account was disabled, so no attachment is added and no activation link would lead anywhere. Reactivating a person is a separate gesture, taken from their sheet, and it comes first.`,
            };
        case 'PRIVILEGE_ESCALATION':
            return {
                title: c('Onboarding invite refusal').t`One of the roles picked goes beyond your own rights.`,
                guidance: c('Onboarding invite refusal')
                    .t`A role is granted only up to what you hold yourself. The selector at step 2 already disables the ones you cannot grant, so reaching this refusal means your own membership changed while this journey was open. Go back to step 2 and pick a role that is still offered. The account concerned is named below.`,
                detail: server,
            };
        case 'ROLE_NOT_FOUND':
            // Family 2, not family 3: the refusal is about a referential that moved, not
            // about the actor, and a gesture on this journey repairs it.
            return {
                title: c('Onboarding invite refusal').t`The role picked at step 2 no longer exists.`,
                guidance: c('Onboarding invite refusal')
                    .t`It has left the referential since it was picked, and nothing brings a deleted role back. Go back to step 2 and pick another one. Beware that the list was loaded when this journey opened and still offers the one that has gone: picking it again would refuse again. The identifier of the missing role is in the answer below.`,
                detail: server,
            };
        default:
            return null;
    }
};

/**
 * Family 3: the refusal is about the actor, not the draft. Rendered after the click:
 * `viewer` exposes the role but not the permissions, so no screen can pre-empt it.
 */
const actorRefusal = (code: string | null, server: string): Refusal | null => {
    switch (code) {
        case 'FORBIDDEN_OUT_OF_SCOPE':
            // Three different objects answer with this code (an account, the contact,
            // a dashboard), so the wording covers the three rather than naming one.
            return {
                title: c('Onboarding invite refusal').t`Part of this invitation is outside your portfolio.`,
                guidance: c('Onboarding invite refusal')
                    .t`The same answer covers three things: an account of the selection that is not entrusted to you, the contact picked at step 1, or a dashboard hanging from an account that has left the selection. The answer below says which. Ask for the account to be added to your portfolio, or hand the onboarding over to whoever administers it.`,
                detail: server,
            };
        case 'MISSING_PERMISSION':
            return {
                title: c('Onboarding invite refusal')
                    .t`Your role does not let you manage the users of one of these accounts.`,
                guidance: c('Onboarding invite refusal')
                    .t`Inviting somebody onto an account requires the right to manage its users, and this screen cannot know beforehand whether you hold it — which is why the button was offered. Ask for that right, or hand the onboarding over to whoever administers the account. The account concerned is named below.`,
                detail: server,
            };
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Onboarding invite refusal').t`Your session no longer carries an active membership.`,
                guidance: c('Onboarding invite refusal')
                    .t`Sign in again, then start the journey over. The draft lives on this screen alone, so note the accounts, the roles and the perimeters picked before leaving: they are not saved anywhere.`,
            };
        default:
            return null;
    }
};

/**
 * Family 4: a draft the screen should never have let compose. None is an operator
 * mistake; reaching one means a guard did not hold. They share one guidance (report it,
 * retrying would loop) and differ only in title.
 */
const malformedDraftRefusal = (code: string | null, server: string): Refusal | null => {
    const title = ((): string | null => {
        switch (code) {
            case 'DUPLICATE_ATTACHMENT':
                return c('Onboarding invite refusal').t`The same account appears twice in the selection.`;
            case 'NO_TARGET_ORGANIZATION':
                return c('Onboarding invite refusal').t`This invitation carries no account at all.`;
            case 'UNKNOWN_MARKET_SCOPE':
                return c('Onboarding invite refusal').t`A line carries a perimeter the platform does not know.`;
            case 'MARKETS_WITH_UNRESTRICTED_SCOPE':
                return c('Onboarding invite refusal').t`A line lists markets while its perimeter is the whole account.`;
            case 'EMPTY_MARKET_RESTRICTION':
                return c('Onboarding invite refusal').t`A line restricts the perimeter to no market at all.`;
            case 'MARKET_NOT_IN_SCOPE':
                return c('Onboarding invite refusal').t`A line lists markets that do not belong to its account.`;
            case 'BACKOFFICE_ROLE_NOT_ALLOWED':
                return c('Onboarding invite refusal').t`A back-office role was picked on a client account.`;
            default:
                return null;
        }
    })();
    if (!title) {
        return null;
    }
    return {
        title,
        guidance: c('Onboarding invite refusal')
            .t`This is a defect of the journey rather than a mistake of yours: the screen let a draft be composed that its own checks should have refused. Sending again would refuse again, since it would compose the same thing. Everything travels in one transaction, so nothing has been created and nothing has to be cleaned up — start the onboarding over, and report the answer below, which is what identifies the defect.`,
        detail: server,
    };
};

/** The refusal, told to the operator who has to unblock it. */
export const inviteRefusalOf = (error: unknown): Refusal => {
    const code = refusalCode(error);
    const server = serverAnswer(error);
    const known =
        contactRefusal(code) ??
        dataStateRefusal(code, server) ??
        actorRefusal(code, server) ??
        malformedDraftRefusal(code, server);
    if (known) {
        return known;
    }
    return {
        title: c('Onboarding invite refusal').t`The invitation was refused. Nothing was created.`,
        guidance: c('Onboarding invite refusal')
            .t`This refusal is not one the screen knows how to read, so nothing here says what to do about it. Everything is sent in a single transaction, so the person has not been half-created — you can safely start over. The server's answer is below: it is what the support desk needs.`,
        // Server's sentence on an unknown code: without it the operator has nothing to report.
        detail: server,
    };
};
